import type { PhaseRecord } from '~/models/phase'
import type { ProjectRecord } from '~/models/project'
import { SCHEMA_V3_STORES, STORE, type StoreDefinition } from '../schema'
import { type DomainBackfillPlan, planDomainBackfill } from './backfillDomains'
import type { Migration, MigrationContext } from './index'

/**
 * Version 3 — the seven stores the opt-in domain model needs, and the backfill
 * that carries every existing project into it (D-090, D-092).
 *
 * ────────────────────────────────────────────────────────────────────────────
 *  THIS IS AN ORDINARY MIGRATION, AND THAT IS A CLAIM WORTH JUSTIFYING
 * ────────────────────────────────────────────────────────────────────────────
 *
 *  Version 2 was exceptional: it rewrote records the user cannot replace. This
 *  one is **additive** — it creates stores and writes new records, and touches no
 *  existing record at all. Nothing here can corrupt a phase, a weigh-in or a
 *  review, because nothing here writes to those stores.
 *
 *  The v2 discipline still applies in full, because it is what makes the claim
 *  checkable rather than hopeful:
 *
 *  **Nothing is awaited.** `run` is synchronous by signature (D-028): every
 *  request is issued here and its continuation runs in `onsuccess`, inside the
 *  same `versionchange` transaction. Awaiting even one non-IndexedDB promise
 *  would let the transaction auto-commit half-applied, with the stores created
 *  and the backfill missing.
 *
 *  **Nothing is repaired by hand.** If any step throws — including inside a
 *  success handler — the exception aborts the transaction and IndexedDB rolls
 *  every change back atomically, leaving the stored version at 2. That is the
 *  recovery path, and `boot.ts` reopens at the prior version when it happens.
 */
export const migrationV3: Migration = {
  to: 3,
  describe:
    'Add the domain, nutrition, daily-check, reminder, wearable, AI-proposal and connection stores, and give every existing project a body-composition domain goal',
  run({ database, transaction }: MigrationContext): void {
    for (const definition of SCHEMA_V3_STORES) {
      createStore(database, definition)
    }
    backfill(transaction)
  },
}

function createStore(database: IDBDatabase, definition: StoreDefinition): void {
  if (database.objectStoreNames.contains(definition.name)) return

  const store = database.createObjectStore(definition.name, { keyPath: definition.keyPath })
  for (const index of definition.indexes) {
    /* A compound index needs the array form passed through unchanged; the cast
       covers both shapes because `createIndex` accepts either. */
    store.createIndex(index.name, index.keyPath as string | string[])
  }
}

/**
 * Read the projects and phases, plan the backfill, and write it.
 *
 * The reads are issued together and the plan is made once the last one lands, so
 * the whole thing is one pass rather than a chain of dependent round trips.
 * `planDomainBackfill` is shared with the backup payload migration — see the note
 * there for why the decision lives outside this file.
 *
 * `domainGoals` is written to via `transaction.objectStore`, which is safe here
 * because the store was created earlier in this same `versionchange` transaction.
 */
function backfill(transaction: IDBTransaction): void {
  const projects = transaction.objectStore(STORE.projects).getAll()
  const phases = transaction.objectStore(STORE.phases).getAll()

  let outstanding = 2
  const arrived = (): void => {
    outstanding -= 1
    if (outstanding > 0) return

    const plan = planDomainBackfill({
      projects: projects.result as ProjectRecord[],
      phases: phases.result as PhaseRecord[],
    })

    // A fresh install has no project, so there is no domain to carry. Setup
    // creates the first domain goals.
    if (plan !== undefined) writePlan(transaction, plan)
  }

  projects.onsuccess = arrived
  phases.onsuccess = arrived
}

function writePlan(transaction: IDBTransaction, plan: DomainBackfillPlan): void {
  const store = transaction.objectStore(STORE.domainGoals)
  for (const goal of plan.domainGoals) store.put(goal)
}
